import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import fundersWidgetMapper from '../../../../components/Widgets/FundersWidget/fundersWidgetMapper';
import nodePartnersWidgetMapper from '../../../../components/Widgets/PartnersWidget/nodePartnersWidgetMapper';
import {
  profileTeamWidgetMapper,
  profileExternalTeamWidgetMapper,
} from '../../../../components/Widgets/TeamWidget/profileTeamWidgetMapper';
import type { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  DateTime,
  FundersWidget,
  ParagraphsRenderer,
  PartnersWidget,
  RelatedContent,
  Tags,
  TeamWidget,
} from '../../../../components';

import './style.scss';
import { RELATED_ARTICLES_HITS_PER_PAGE } from '../../../../config/constants';

interface NodeProjectPageProps {
  entity: ImmutableEntity<NodeProject>;
  className?: string;
}

const NodeProjectPage: React.FC<NodeProjectPageProps> = ({ entity, className }) => {
  const { t } = useTranslation();

  const project = entity.toJS();

  const nid = project.nid[0].value;
  const langcode = project.langcode[0].value;
  const title = project.title[0].value;
  const body = project.body[0]?.processed;
  const popularAbstract = project.field_popular_abstract[0]?.processed;
  const scientificAbstract = project.field_scientific_abstract[0]?.processed;
  const departments = project.field_departments;
  const themes = project.field_themes;
  const duration = project.field_duration[0];
  const durationStartDate = duration?.value;
  const durationEndDate = duration?.end_value;
  const media = project.field_media_item[0];
  const caption = media?.entity?.field_caption[0]?.value;
  const paragraphs = project.field_paragraphs;
  const paragraphsSidebar = project.field_paragraphs_sidebar;

  // Profiles can be (temporarily) de-activated, so we check if the entity exists.
  const researchLeaders = project.field_research_leaders.filter((reference) => 'entity' in reference);
  const secondaryResearchLeaders = project.field_secondary_project_leaders.filter((reference) => 'entity' in reference);
  const projectTeam = project.field_project_team.filter((reference) => 'entity' in reference);
  const externalStaff = project.field_external_staff.filter((reference) => 'entity' in reference);

  // An extra failsafe incase a CMS user deletes a funder or partner.
  const funders = project.field_funders.filter((reference) => 'entity' in reference);
  const partners = project.field_partner.filter((reference) => 'entity' in reference);

  const classes = classNames('Node', 'NodeProjectPage', className);
  const sidebarColClasses = classNames({
    'col-12 col-md-5 col-lg-4 offset-lg-1': paragraphs.length > 0 || scientificAbstract,
    'col-12 col-md-7': paragraphs.length === 0 && !scientificAbstract,
  });

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeProjectPage__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} />
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-10 col-lg-9">
              <div className="NodeProjectPage__hero__text-wrapper">
                <div className="NodeProjectPage__hero__text">
                  <h1 className="NodeProjectPage__title" itemProp="title">
                    {title}
                  </h1>

                  {body && (
                    <div className="NodeProjectPage__body">
                      <WysiwygField
                        className="body--large"
                        property="schema:text"
                        html={body}
                      />
                    </div>
                  )}

                  {popularAbstract && (
                    <div className="NodeProjectPage__popularAbstract">
                      <WysiwygField
                        className="body--large"
                        property="schema:text"
                        html={popularAbstract}
                      />
                    </div>
                  )}

                  {(departments.length > 0 || themes.length > 0) && (
                    <div className="NodeProjectPage__tags">
                      {departments.length > 0 && (
                        <Tags
                          title={t('taxonomies.departments.title', { count: departments.length })}
                          items={taxonomyTagEntityMapper(departments)}
                        />
                      )}
                      {themes.length > 0 && (
                        <Tags
                          title={t('taxonomies.themes.title', { count: themes.length })}
                          items={taxonomyTagEntityMapper(themes)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(durationStartDate && durationEndDate) && (
              <div className="col-12 col-lg-3">
                <div className="NodeProjectPage__hero__duration-wrapper">
                  <div className="NodeProjectPage__hero__duration">
                    <DateTime
                      title={t('content_types.project.duration.title')}
                      date={new Date(durationStartDate)}
                      endDate={new Date(durationEndDate)}
                      translationKey="content_types.project.duration.datetime"
                      langcode={langcode as unknown as 'nl' | 'en'}
                    />
                  </div>
                </div>
              </div>
            )}

            {media && (
              <div className="col-12">
                <div className="NodeProjectPage__hero__media-wrapper">
                  <div className="NodeProjectPage__hero__media">
                    <DynamicMediaItem
                      media={media}
                      imageAspectRatio="2_1"
                    />
                    {/* We render the caption outside, to prevent layout shifting */}
                    {caption && (
                      <p className="caption">{caption}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <Container>
        <div className="row">
          {(paragraphs.length > 0 || scientificAbstract) && (
            <div className="col-12 col-md-7">
              {scientificAbstract && (
                <div className="NodeProjectPage__scientificAbstract">
                  <h2>{t('content_types.project.scientific_abstract')}</h2>
                  <WysiwygField
                    property="schema:text"
                    html={scientificAbstract}
                  />
                </div>
              )}
              <ParagraphsRenderer
                className="NodeProjectPage__paragraphs"
                paragraphs={paragraphs}
                noBootstrap
              />
            </div>
          )}

          <div className={sidebarColClasses}>
            <div
              role="complementary"
              className="NodeProjectPage__sidebar"
              aria-label={t('general.related_info')}
            >
              {(researchLeaders.length > 0 || projectTeam.length > 0) && (
                <TeamWidget
                  context="project"
                  leads={profileTeamWidgetMapper(researchLeaders)}
                  secondaryLeads={profileExternalTeamWidgetMapper(secondaryResearchLeaders)}
                  members={profileTeamWidgetMapper(projectTeam)}
                  externalStaff={profileExternalTeamWidgetMapper(externalStaff)}
                />
              )}

              {(funders && funders.length > 0) && (
                <FundersWidget
                  funders={fundersWidgetMapper(funders)}
                />
              )}

              {(partners && partners.length > 0) && (
                <PartnersWidget
                  partners={nodePartnersWidgetMapper(partners)}
                />
              )}

              <ParagraphsRenderer className="NodeProjectPage__paragraphs" paragraphs={paragraphsSidebar} noBootstrap />
            </div>
          </div>
        </div>
      </Container>

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <div id="articles" className="NodeProjectPage__articles">
                <RelatedContent
                  title={t('content_types.article.related_content')}
                  contentType="article"
                  hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
                  filterNames={['projects_nid']}
                  filterValues={[nid]}
                  langcode={langcode}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeProjectPage;
